/**
 * "Continue with Google" / "Continue with Apple" (easy-signup handoff, 2026-10-04).
 *
 * Cognito's own OAuth flow, through the sign-in domain (auth.saddle-sites.com): the browser goes
 * to the provider, the provider returns to Cognito, Cognito returns here (/auth/callback) with a
 * one-time code, and that code — with the PKCE verifier only this tab holds — is traded for the
 * account's tokens, which the SDK then keeps as if it had signed them in (auth.ts `adoptTokens`).
 *
 * Which providers are on is decided at deploy (scripts/deploy-web.sh reads the pool), so a
 * button is never shown for a provider the pool would refuse.
 */
import { adoptTokens, claimsOf, type SessionUser, type Tokens } from './auth';

export type Provider = 'google' | 'apple';

/** Cognito's names for them, as `identity_provider=` takes them. */
const COGNITO_NAME: Record<Provider, string> = { google: 'Google', apple: 'SignInWithApple' };
const PENDING_KEY = 'saddle.oauth';
/** A trip to the provider that has not come back in this long is abandoned. */
const PENDING_MAX_AGE_MS = 30 * 60 * 1000;

interface Pending {
  state: string;
  verifier: string;
  provider: Provider;
  from?: string;
  at: number;
  /** Already retried once after Cognito's "Already found an entry" (see `finishSocial`). */
  retried?: boolean;
}

function authDomain(): string {
  return (process.env.EXPO_PUBLIC_AUTH_DOMAIN ?? '').replace(/\/+$/, '');
}

function clientId(): string {
  return process.env.EXPO_PUBLIC_COGNITO_CLIENT_ID ?? '';
}

/** The providers the pool has on, read from the build (`VITE_AUTH_PROVIDERS=google,apple`). */
export function socialProviders(): Provider[] {
  if (!authDomain()) return [];
  const listed: string = process.env.EXPO_PUBLIC_AUTH_PROVIDERS ?? '';
  return listed
    .split(',')
    .map((p: string) => p.trim())
    .filter((p): p is Provider => p === 'google' || p === 'apple');
}

export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

function base64url(bytes: Uint8Array): string {
  let s = '';
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** The address that starts the trip: Cognito's authorize, naming the provider so no Cognito page shows. */
export async function authorizeUrl(provider: Provider, from?: string, retried = false): Promise<string> {
  const pending: Pending = { state: randomString(), verifier: randomString(), provider, at: Date.now(), ...(from ? { from } : {}), ...(retried ? { retried } : {}) };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  const params = new URLSearchParams({
    identity_provider: COGNITO_NAME[provider],
    client_id: clientId(),
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri(),
    state: pending.state,
    code_challenge: await challengeFor(pending.verifier),
    code_challenge_method: 'S256',
  });
  return `${authDomain()}/oauth2/authorize?${params.toString()}`;
}

export type SocialResult =
  | { ok: true; user: SessionUser; provider: Provider; from?: string; created: boolean }
  | { ok: false; provider?: Provider; from?: string; reason: string; retryAt?: string };

function readPending(): Pending | null {
  try {
    const p = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? 'null') as Pending | null;
    return p && Date.now() - p.at < PENDING_MAX_AGE_MS ? p : null;
  } catch {
    return null;
  }
}

/**
 * Whether the account was made just now: Cognito puts when a provider identity was first seen in
 * the ID token (`identities[].dateCreated`). New is a registration; an old one is a sign-in.
 */
function createdJustNow(idToken: string): boolean {
  const ids = claimsOf(idToken).identities;
  const first = Array.isArray(ids) ? (ids[0] as { dateCreated?: string | number } | undefined) : undefined;
  const at = Number(first?.dateCreated);
  return Number.isFinite(at) && Date.now() - at < 10 * 60 * 1000;
}

/**
 * Back from the provider: check the state, trade the code, keep the session.
 *
 * One known Cognito answer is retried, once: the first Google or Apple sign-in for an email that
 * already has an account is joined to it before sign-up (infra/lambda/shared/socialLink.ts), and
 * Cognito may then refuse THAT attempt with "Already found an entry for username". The second
 * goes straight through as the joined account — the provider remembers the person, so it is a
 * blink, not a second consent screen.
 */
export async function finishSocial(search: string, fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<SocialResult> {
  const params = new URLSearchParams(search);
  const pending = readPending();
  const base = { provider: pending?.provider, from: pending?.from };
  const error = params.get('error_description') ?? params.get('error');
  if (error) {
    if (pending && !pending.retried && /already found an entry for username/i.test(error)) {
      return { ok: false, ...base, reason: 'linked_retry', retryAt: await authorizeUrl(pending.provider, pending.from, true) };
    }
    sessionStorage.removeItem(PENDING_KEY);
    return { ok: false, ...base, reason: /access_denied|cancel/i.test(error) ? 'cancelled' : 'provider_error' };
  }
  const code = params.get('code');
  if (!pending || !code || params.get('state') !== pending.state) {
    sessionStorage.removeItem(PENDING_KEY);
    return { ok: false, ...base, reason: 'state_mismatch' };
  }
  sessionStorage.removeItem(PENDING_KEY);
  const res = await fetchImpl(`${authDomain()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId(),
      code,
      redirect_uri: redirectUri(),
      code_verifier: pending.verifier,
    }).toString(),
  }).catch(() => null);
  if (!res?.ok) return { ok: false, ...base, reason: 'token_exchange' };
  const body = (await res.json()) as { id_token?: string; access_token?: string; refresh_token?: string };
  if (!body.id_token || !body.access_token) return { ok: false, ...base, reason: 'token_exchange' };
  const tokens: Tokens = { IdToken: body.id_token, AccessToken: body.access_token, RefreshToken: body.refresh_token };
  return { ok: true, user: adoptTokens(tokens), provider: pending.provider, from: pending.from, created: createdJustNow(body.id_token) };
}
